import React, { Suspense } from 'react';

import { ErrorBoundary } from '../../../controllers/error-boundary';
import type { SearchDialogProps } from './index';
import { SearchDialogUnavailable } from './unavailable';

const loadSearchDialog = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/quick-find-dialog" */ './index'
	);

/**
 * Warms the dialog chunk without mounting it. Safe to call repeatedly and while the boundary
 * below is already loading — the module system caches the chunk promise, so no memoisation here.
 */
export const preloadSearchDialog = (): void => {
	// Swallowed: nothing is rendered yet, so there is nothing to degrade. A failure the user can
	// notice is surfaced by the error boundary when the dialog is opened.
	void loadSearchDialog().catch(() => {});
};

// `Object.assign` because `LazyExoticComponent` does not declare `displayName`, so assigning it
// directly does not typecheck. Keeps the component stack readable in the logged exception.
const SearchDialogLazy = Object.assign(
	React.lazy(() => loadSearchDialog().then(({ SearchDialog }) => ({ default: SearchDialog }))),
	{ displayName: 'lazy(SearchDialog)' },
);

/**
 * Lazy boundary around `SearchDialog`, which only mounts while Quick Find is open. A static
 * import kept its whole module graph — the bulk of the Quick Find chunk — in the eager bundle on
 * every page load.
 *
 * The `ErrorBoundary` is load-bearing, not defensive: a failed chunk request throws during render,
 * and without it that would take down the whole Quick Find surface, input included.
 *
 * A failed chunk cannot recover within the session (the bundler caches the chunk promise and
 * `React.lazy` latches the rejection), so the fallback offers no retry.
 *
 * The Suspense fallback is `null`: anything drawn on the dialog surface flashes, because the
 * surface itself paints immediately even when its contents are delayed. The chunk is preloaded on
 * hover or focus, so most opens never suspend at all.
 */
export const SearchDialogAsync = (props: SearchDialogProps): React.JSX.Element => (
	<ErrorBoundary section="QuickFindSearchDialogAsync" fallback={<SearchDialogUnavailable />}>
		<Suspense fallback={null}>
			<SearchDialogLazy {...props} />
		</Suspense>
	</ErrorBoundary>
);
